import { supabase } from './supabase';
import { emailTargetUrl } from './appUrl';
import { deepLinkUrl, transferLinkId } from './deepLink';
import { getCompanyName } from './tenant';
import { canReconcileInvoice } from './permissions';

// Session 62b — Issue #147: filters opted-out recipients before insert. Single choke
// point for all 34 sendNotification() call sites, so no consuming surface or writer
// needs its own suppression logic. Requires the "Authenticated users can read all
// preferences" SELECT policy (added same session) — the caller's own session can't
// see other users' preference rows under the table's default self-scoped RLS.
// RETURN CONTRACT, added Session 78n8.3 -- the #258 sibling gap, which had been
// open since Session 74m and is fixed here because #413 adds a call site to this
// very function.
//
// WHAT WAS WRONG, in plain words: this function told a lie by omission. It wrote
// the notification row and threw away the answer, so "the database refused the
// write" and "the notification is in the bell" looked identical to all 34 places
// that call it. That is exactly how #258 came to display "Your manager has been
// notified" to somebody whose manager had not been notified.
//
// It now returns { ok, delivered, reason?, error? } from EVERY path, and the
// shape is uniform on purpose -- a function that returns an object sometimes and
// undefined other times pushes an optional-chain onto every caller and gets one
// of them wrong. Existing callers ignore the return and behave exactly as
// before; nothing here changes when the write succeeds.
//
// `ok: true, delivered: 0` is a real and correct outcome, not a failure: it
// means nobody was owed this notification -- an empty recipient list, or
// everybody opted out. Callers that want to say "we told them" should check
// `delivered`, not `ok`.
export async function sendNotification(userIds, title, message, event_type, domain = 'general', link_to = null, link_id = null) {
  if (!userIds || userIds.length === 0) return { ok: true, delivered: 0, reason: 'no recipients' };

  const { data: optedOut } = await supabase
    .from('user_notification_preferences')
    .select('user_id')
    .eq('event_type', event_type)
    .eq('enabled', false)
    .in('user_id', userIds);

  const optedOutIds = new Set((optedOut || []).map(r => r.user_id));
  const recipients = userIds.filter(uid => !optedOutIds.has(uid));
  if (recipients.length === 0) return { ok: true, delivered: 0, reason: 'all opted out' };

  const records = recipients.map(uid => ({
    user_id: uid,
    title,
    message,
    event_type,
    domain,
    read: false,
    link_to,
    link_id: link_id != null ? String(link_id) : null,
  }));
  const { error } = await supabase.from('notifications').insert(records);
  if (error) {
    // Logged as well as returned. Most of the 34 existing call sites do not read
    // the return value, so without this line a rejected insert would still be
    // invisible everywhere it already is -- the console entry is what makes the
    // failure findable on a surface nobody has updated yet.
    console.error('[#258 sibling] notification insert failed', {
      event_type, domain, recipients: recipients.length, error,
    });
    return { ok: false, delivered: 0, error };
  }
  return { ok: true, delivered: records.length };
}

export async function getAdminIds() {
  const { data } = await supabase.from('users').select('id').eq('role', 'admin').eq('is_active', true);
  return (data || []).map(u => u.id);
}

// #650, Session 79.22b -- everybody who may approve at ANY branch: canApprove()
// and canApproveTransfer() let admin and executive through unconditionally. The
// ids twin of getAdminExecutiveEmails() below; getAdminIds() is admin alone.
export async function getAdminExecutiveIds() {
  const { data } = await supabase.from('users').select('id').in('role', ['admin', 'executive']).eq('is_active', true);
  return (data || []).map(u => u.id);
}

// ── WHO RECONCILES INVOICES — #301, Session 79.12 ────────────────────────────
//
// ** THE CANDIDATES ARE FETCHED AND THE REAL PREDICATE DECIDES, rather than
// the rule being rewritten as a query. ** canReconcileInvoice() is
// `isAdmin || isExecutive || ((isManager || isPurchasing) &&
// profile.invoice_reconciliation_enabled === true)`, and writing that as SQL
// would be a SECOND IMPLEMENTATION of a permission — locked rule 8, and the
// copy that drifts is always the one nobody is editing. canApprove.js already
// uses this shape: fetch the plausible rows, filter with the one definition.
//
// The `=== true` matters and is why the flags are built per row rather than
// assumed: a manager whose toggle has never been set carries null, and null
// is NOT permission. Getting that backwards would mail every manager in the
// company about every completed delivery.
export async function getInvoiceReconcilerIds() {
  const { data } = await supabase
    .from('users')
    .select('id, role, invoice_reconciliation_enabled')
    .in('role', ['admin', 'executive', 'manager', 'purchasing'])
    .eq('is_active', true);
  return (data || [])
    .filter(u => canReconcileInvoice({
      isAdmin: u.role === 'admin',
      isManager: u.role === 'manager',
      isExecutive: u.role === 'executive',
      isPurchasing: u.role === 'purchasing',
      profile: u,
    }))
    .map(u => u.id);
}

export async function getBranchManagerIds(branchId) {
  if (!branchId) return [];
  const { data } = await supabase.from('users').select('id').eq('role', 'manager').eq('branch_id', branchId).eq('is_active', true);
  return (data || []).map(u => u.id);
}

// ── Material-request approvers — #396, Session 78n4 ──
//
// getBranchManagerIds() above is NOT this and must not be changed into it. It is
// still correct for the paths that genuinely mean "tell the managers here":
// receiving, the requests panel, low-stock. Approval is a narrower audience now,
// so it gets its own lookup rather than a redefinition that would silently change
// three unrelated notifications.
//
// The approver set is exactly two people, and it MUST agree with canApprove() in
// src/lib/canApprove.js — that predicate decides whether the Approve button
// renders, this query decides who gets told. If they disagree, somebody is
// notified about a request they cannot action, or can action one they were never
// told about. Change both or neither.
//
//   * every BRANCH manager at the branch, always, switch or no switch;
//   * the manager OF THAT DEPARTMENT, if they have been granted the capability.
//
// A request with no department (its requester has none set) resolves to the
// branch manager alone, which is correct rather than an error — there is no
// department manager to route it to.
async function fetchApprovers(branchId, departmentId, column) {
  if (!branchId) return [];
  const { data } = await supabase
    .from('users')
    .select(`${column}, manager_scope, department_id, can_approve_requests`)
    .eq('role', 'manager')
    .eq('branch_id', branchId)
    .eq('is_active', true);

  return (data || [])
    .filter(u =>
      u.manager_scope === 'branch' ||
      (u.can_approve_requests === true &&
       departmentId != null &&
       String(u.department_id) === String(departmentId))
    )
    .map(u => u[column])
    .filter(Boolean);
}

export async function getRequestApproverIds(branchId, departmentId) {
  return fetchApprovers(branchId, departmentId, 'id');
}

// ── Stock-audit recipients — #500, Session 79.1 ──
//
// WHAT WAS WRONG, in plain words. When somebody drove a count negative, or wrote
// something off as damaged, Stede told EVERY manager at the branch — including the
// manager of a department with no connection to the stock in question. Measured at
// Session 79: a Production write-off notified four people, one of whom runs 811.
// The screen had promised something narrower: "Your manager will be notified to
// audit this item." Singular.
//
// Nothing was exposed that those managers could not already see, so this was noise
// rather than a permission fault. Noise is still the thing that teaches people to
// ignore the bell, and this app asks them to trust it for approvals and deliveries.
// A negative count is meant to be rare; a damaged write-off is routine.
//
// THE AUDIENCE, and why it is not getRequestApproverIds():
//
//   * every BRANCH manager at the branch — always accountable for the count;
//   * the manager OF THE DEPARTMENT THE ACTOR WORKS IN.
//
// getRequestApproverIds() looks almost right and is wrong here, because it also
// requires `can_approve_requests`. That switch is about authorising SPENDING. A
// department manager who cannot approve a purchase is still the person who owns
// the shelf, so gating an audit alert on a spending permission would silently drop
// the one recipient this exists to reach. Different question, different lookup —
// the same reasoning the #396 comment above gives for not folding
// getBranchManagerIds() into the approver query.
//
// THE DEPARTMENT IS THE ACTOR'S, NOT THE ITEM'S. Inventory items carry no
// department in this schema; the person who moved the stock does. So the alert
// follows whoever touched the count, which is also who would be asked about it.
//
// A null department resolves to the branch managers alone, which is correct rather
// than an error — there is no department manager to route it to, and guessing one
// is exactly the inference #272 exists to remove.
export async function getStockAuditRecipientIds(branchId, departmentId) {
  if (!branchId) return [];
  const { data } = await supabase
    .from('users')
    .select('id, manager_scope, department_id')
    .eq('role', 'manager')
    .eq('branch_id', branchId)
    .eq('is_active', true);

  return (data || [])
    .filter(u =>
      u.manager_scope === 'branch' ||
      (departmentId != null && String(u.department_id) === String(departmentId))
    )
    .map(u => u.id)
    .filter(Boolean);
}

export async function getRequestApproverEmails(branchId, departmentId) {
  return fetchApprovers(branchId, departmentId, 'email');
}

// #683, Session 79.22b.4.3 -- THE ADDRESSES OF PEOPLE ALREADY CHOSEN. For a
// notification whose bell and email must reach the same people: the caller
// builds ONE list of user ids for the bell, and this turns exactly that list
// into addresses. The write-off looked its audience up twice -- approvers for
// the bell, every manager and the admins for the email -- and told 5 people by
// bell and 11 by email about the same loss.
export async function getEmailsForUserIds(ids) {
  const list = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean).map(String))];
  if (!list.length) return [];
  const { data } = await supabase.from('users').select('email').in('id', list).eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

// New Session 74k7 — Issue #241. In-app counterpart to getPurchasingEmails(),
// which had no ID-returning sibling. An off-catalog arrival has to reach
// purchasing in-app, not only by email, because the catalog decision that
// follows is theirs. Written rather than assumed: a Gate 6 read of this file
// confirmed no purchasing ID lookup existed anywhere, so a sendNotification()
// call built on the assumption that one did would have silently reached zero
// recipients — the same shape as #137, #142 and #144.
export async function getPurchasingIds() {
  const { data } = await supabase.from('users').select('id').eq('role', 'purchasing').eq('is_active', true);
  return (data || []).map(u => u.id);
}

export async function getAdminEmails() {
  const { data } = await supabase.from('users').select('email').eq('role', 'admin').eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

export async function getBranchManagerEmails(branchId) {
  if (!branchId) return [];
  const { data } = await supabase.from('users').select('email').eq('role', 'manager').eq('branch_id', branchId).eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

// Added Session 55 — trigger #2 of the material-request email lifecycle (approve → purchasing).
// Mirrors getAdminEmails()/getBranchManagerEmails() exactly; no prior role-based email lookup
// for 'purchasing' existed anywhere in this file.
export async function getPurchasingEmails() {
  const { data } = await supabase.from('users').select('email').eq('role', 'purchasing').eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

// New Session 74b2 — Issue #191. Admin+executive audience for invoice discrepancy
// alerts; distinct from getAdminEmails() (admin only) since the decision for this
// trigger was admin AND executive, not admin alone.
export async function getAdminExecutiveEmails() {
  const { data } = await supabase.from('users').select('email').in('role', ['admin', 'executive']).eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

// New Session — Issue #210. Broader than getPurchasingEmails() (purchasing role
// only) — covers everyone who can actually act on Mark Ordered again after a
// denial: admin, executive, and the purchasing role.
export async function getPurchasingQueueActionEmails() {
  const { data } = await supabase.from('users').select('email').in('role', ['admin', 'executive', 'purchasing']).eq('is_active', true);
  return (data || []).map(u => u.email).filter(Boolean);
}

// #395, Session 78n8.2 -- `options` and a RETURN VALUE, both added here rather
// than in a second function, so there is one send path rather than two.
//
// options: { replyTo, bcc, attachments } -- the three fields #405 and this
// session added to functions/send-email.js. Omitted keys are never sent, so a
// notification that passes no options produces byte-for-byte the request it
// produced before. That property is asserted in src/lib/mailPayload.test.js.
//
// WHY IT NOW RETURNS SOMETHING, and this is the part that matters: every send
// is SUPPRESSED outside production, and until now that was indistinguishable
// from a successful one because this function returned undefined either way.
// For a staff notification that is harmless. For a PURCHASE ORDER it is not --
// the Mark as ordered sheet would tell Jennifer the order had gone to the
// vendor while nothing had left the building. Callers that ignore the return
// behave exactly as before.
//
// Returns { ok, suppressed, error }.
export async function sendEmail(to, subject, html, options = {}) {
  if (!to || (Array.isArray(to) && to.length === 0)) {
    return { ok: false, suppressed: false, error: 'No recipients' };
  }
  if (process.env.NODE_ENV !== 'production') {
    console.log('DEV MODE — email suppressed. Would have sent to:', to, '| Subject:', subject);
    return { ok: true, suppressed: true, error: null };
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const payload = { to, subject, html };
    if (options.replyTo) payload.reply_to = options.replyTo;
    if (options.bcc) payload.bcc = options.bcc;
    if (options.attachments) payload.attachments = options.attachments;

    const response = await fetch('/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const text = await response.text();
      console.error('Email send failed:', text);
      // The function answers with { error } on every refusal it authors, so the
      // caller can show the vendor-facing reason rather than a status code.
      let message = 'Email send failed';
      try { message = JSON.parse(text)?.error || message; } catch { /* not JSON */ }
      return { ok: false, suppressed: false, error: message };
    }
    return { ok: true, suppressed: false, error: null };
  } catch (err) {
    console.error('Email send error:', err);
    return { ok: false, suppressed: false, error: err?.message || 'Email send error' };
  }
}

// ── SHARED TEMPLATE HELPERS ──────────────────────────────────────────────────

// Issue #263 — this is the deliberate carve-out, not a site that was missed. The three
// password paths now derive their origin from wherever the person is standing; these CTAs
// must not. They are rendered by whoever triggered the notification and delivered to
// someone else, so a window.location.origin here would put a preview-branch URL in mail
// sent to real staff. See src/lib/appUrl.js — both rules live there together.
//
// AMENDED at Session 79.7 for #507. The paragraph above stays, because its
// DANGER is still real and still guarded against: production mail is still
// built from the canonical host, unconditionally, and never from the sender's
// origin. What it got wrong is treating "not the sender's origin" as the rule,
// when the real rule is "an address backed by the same database as the record".
// On production those are the same sentence. On staging they are opposites, and
// Colby pressed a button in a staging test email and landed on the live site.
//
// emailTargetUrl() owns that decision now, and it is called at RENDER TIME
// rather than captured in a module constant, so the value cannot be frozen
// before the page knows where it is running.

// #126, Session 78h — gate decisions E5 and E6.
//
// E5: ctaLabel and ctaUrl default to today's generic pair, so a template nobody
// has migrated behaves exactly as it did before. That is what lets the templates
// move one at a time and makes a half-finished migration impossible to break —
// zero regression by construction rather than by care.
//
// E6: the label names the destination in the reader's language, per template.
// "Open Stede" tells the reader nothing about what they will get, and generic
// link text is both an accessibility anti-pattern and a regression against the
// app's own in-app copy, which already says Requests inbox and Purchasing Queue.
//
// E9: one CTA per email, no secondary links. A second button halves the odds of
// the first being pressed.
// #414 + gate decisions M1 and M14, Session 78v. THE STRIP'S COLOUR IS NO
// LONGER A FREE CHOICE.
//
// It used to be an `accentColor` argument, and fourteen templates chose nine
// different values -- six of them in no Stede palette at all, including a
// Purchasing email wearing the Tools blue. Colby's ruling at 78v: the colour
// says WHICH PART OF STEDE the mail came from, using the five locked module
// colours the app already teaches. Taking a module rather than a hex value is
// what makes that structural instead of a convention somebody re-breaks.
//
// THE TEXT COLOUR TRAVELS WITH IT, AND THAT IS NOT A DETAIL. Every template
// shipped white text on this strip. Measured rather than assumed: white is
// 3.39 on Inventory green, 2.17 on Purchasing orange, 3.59 on Tools blue and
// 3.93 on Safety red -- all below the 4.5 readable standard, and the orange
// fails even the relaxed large-text bar. Near-black passes on all four
// (5.66 / 8.82 / 5.33 / 4.87). Invoicing magenta is the only one dark enough
// to take white, at 6.17. So the pair is chosen here and never at a call site.
const MODULE_STRIP = {
  inventory:  { bg: '#1D9E75', fg: '#0F0F0F' },
  purchasing: { bg: '#EF9F27', fg: '#0F0F0F' },
  tools:      { bg: '#378ADD', fg: '#0F0F0F' },
  safety:     { bg: '#E24B4A', fg: '#0F0F0F' },
  invoicing:  { bg: '#B31F7C', fg: '#FFFFFF' },
};

// EXPORTED for one reason: gate decision M6's footer guard is unreachable
// from any of the fourteen templates today, because none of them passes a
// reply-to yet. An untestable guard is the thing this project keeps being
// bitten by - it is indistinguishable from one that does not work. So the
// wrapper is exported and both branches are exercised in
// src/lib/emailTemplates.test.js. Nothing else should call it directly.
export function emailWrapper({ module, icon, eventLabel, bodyContent, ctaLabel, ctaUrl, replyTo }) {
  // An unknown module falls back to Inventory rather than throwing. A
  // notification that fails to render is worse than one wearing the wrong
  // colour, and the wrong colour is visible the first time anybody looks.
  const strip = MODULE_STRIP[module] || MODULE_STRIP.inventory;
  const timestamp = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  // 74z8 — Colby's call: email carries BOTH names, the same lockup the topbar
  // uses. Stede's identity, a divider, then the tenant's.
  const tenant = getCompanyName();

  // #731, Session 79.22b.6d -- THE NOTES ABOUT THIS LAYOUT LIVE HERE, NOT IN IT.
  // They used to be HTML comments inside the string below, so every email
  // carried the team's build notes to anybody who opened its source. The
  // layout's rows, top to bottom: header, event strip, body, button, footer.
  //
  // HEADER -- sentence case doing the same structural work it does in the
  // topbar: Stede reads as a name, the tenant reads as a label. The divider is
  // a literal glyph rather than a bordered element on purpose -- Outlook's
  // word-processor renderer drops or mispositions 1px borders inside inline
  // spans, and a missing rule here would read as a typo.
  //
  // BUTTON -- 78v: padding went 12px -> 14px because the button MEASURED 40px
  // tall on a 375pt phone, under the 44pt tap-target minimum. The gate claimed
  // it was "well above" it; it was not. Caught by measuring the rendered button
  // rather than trusting the mockup, and the number is asserted in
  // emailTemplates.test.js so it cannot quietly shrink again.
  //
  // FOOTER -- gate decision M6, Session 78v, and it is a GUARD rather than a bug
  // fix. The gate claimed the no-reply line was already false on purchase
  // orders; reading the code disproved that -- the purchase order is a separate
  // template in purchaseOrder.js which asks the vendor to "confirm receipt and
  // expected ship date by reply" and never carried this line. So nothing lies
  // today. It is made conditional anyway because the moment any of these
  // fourteen gains a reply-to, this sentence becomes a promise the message
  // breaks, and a guard that cannot be forgotten beats a note in a document
  // that can.
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${tenant} on Stede</title>
</head>
<body style="margin:0;padding:0;background:#F7F7F7;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F7F7F7;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;">

          <tr>
            <td style="background:#0F0F0F;border-radius:10px 10px 0 0;padding:24px 32px;border-bottom:3px solid #39D353;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:22px;font-weight:700;color:#39D353;letter-spacing:0.5px;">Stede</span>
                    <span style="font-size:14px;color:#4A4A4A;margin:0 9px;">&#124;</span>
                    <span style="font-size:12px;color:#9A9A9A;letter-spacing:2px;text-transform:uppercase;">${tenant}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="background:${strip.bg};padding:16px 32px;">
              <span style="font-size:16px;font-weight:700;color:${strip.fg};text-transform:uppercase;letter-spacing:0.5px;">
                ${icon}&nbsp;&nbsp;${eventLabel}
              </span>
            </td>
          </tr>

          <tr>
            <td style="background:#ffffff;padding:32px;border-left:1px solid #E8E8E8;border-right:1px solid #E8E8E8;">
              ${bodyContent}
            </td>
          </tr>

          <tr>
            <td style="background:#ffffff;padding:0 32px 32px;border-left:1px solid #E8E8E8;border-right:1px solid #E8E8E8;text-align:center;">
              <a href="${ctaUrl || emailTargetUrl()}"
                style="display:inline-block;background:#39D353;color:#0F0F0F;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:1px;padding:14px 28px;border-radius:6px;text-decoration:none;">
                ${ctaLabel || 'Open Stede'} →
              </a>
            </td>
          </tr>

          <tr>
            <td style="background:#F0F0F0;border-radius:0 0 10px 10px;padding:16px 32px;border:1px solid #E8E8E8;border-top:none;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-size:11px;color:#9A9A9A;">
                    ${tenant} on Stede &nbsp;·&nbsp; ${timestamp}
                  </td>
                  <td align="right" style="font-size:11px;color:#9A9A9A;">
                    ${replyTo ? `Replies reach ${replyTo}` : 'Do not reply to this email'}
                  </td>
                </tr>
                <tr>
                  <td colspan="2" style="font-size:11px;color:#C0C0C0;padding-top:4px;">
                    © 2026 Stede
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;
}

function detailRow(label, value) {
  if (!value) return '';
  return `
    <tr>
      <td style="padding:8px 0;border-bottom:1px solid #F0F0F0;width:140px;vertical-align:top;">
        <span style="font-size:11px;font-weight:700;color:#9A9A9A;text-transform:uppercase;letter-spacing:0.8px;">${label}</span>
      </td>
      <td style="padding:8px 0 8px 16px;border-bottom:1px solid #F0F0F0;vertical-align:top;">
        <span style="font-size:14px;color:#0F0F0F;">${value}</span>
      </td>
    </tr>
  `;
}

function detailTable(rows) {
  return `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      ${rows}
    </table>
  `;
}

// ── MULTI-ITEM LINE TABLE ────────────────────────────────────────────────────
// Session 79.4, gate Stede_MultiItemEmail_Gate.html, decisions D1-D8.
//
// WHY THIS EXISTS. #353 fixed the DATA half of multi-item email -- before it, a
// three-item request emailed one item and silently dropped two. What it could
// not fix is the SHAPE, because detailRow() has one label column and one value
// column: every line got comma-joined into that single value cell.
//
// MEASURED on the shipping template at 79.4, not estimated. detailRow()'s label
// column is a FIXED 140px at every width, so on a 320px phone the item list gets
// 82px of width and runs 239px tall -- a column of broken text two fingers wide,
// with the word "ITEM" given nearly twice the room the items get. At 375px it
// wraps to six lines. That is what this replaces.
//
// D2 -- this table drops the label column entirely and spans the full body
// width, which is where the room comes from: 82px -> 224px at 320px.
//
// D7 -- a real <table> with inline styles and widths on the cells, and NO media
// query. Dallas reads these in DESKTOP OUTLOOK, which renders with Word's engine
// and ignores media queries outright, so the usual "stack the columns on mobile"
// pattern would simply not run there. One layout that is correct with no CSS
// support at all beats two layouts where the important client gets neither.
const LINE_DISPLAY_LIMIT = 10;

// Only used by the line table below. The older templates interpolate their values
// raw and that is left alone here -- but an off-catalog line is FREE TEXT a person
// typed, and these are new interpolation points, so they are escaped rather than
// adding another unescaped one. The pre-existing raw sites are reported, not
// silently changed, because that is a separate decision from this gate.
function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// `lines` is [{ name, qty, uom, isOffCatalog }]. Returns '' for 0 or 1 lines, so
// a single-item request keeps EXACTLY the layout it had before this change --
// D1, and the same principle #353 used: no cosmetic churn on the common case.
export function lineItemsTable(lines, heading) {
  if (!Array.isArray(lines) || lines.length < 2) return '';

  const total = lines.length;
  const shown = lines.slice(0, LINE_DISPLAY_LIMIT);
  const hidden = total - shown.length;

  const rows = shown.map(line => {
    const name = escapeHtml(line?.name) || 'Item';
    // D8 -- a mixed request is normal, and the approver is agreeing to two
    // different things: a known item at a known price, and something nobody has
    // sourced yet. The tag carries that difference without a second screen.
    const tag = line?.isOffCatalog
      ? ' <span style="display:inline-block;font-size:10px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;background:#FFF3E0;color:#9A5F00;padding:1px 5px;border-radius:3px;">Not in catalog</span>'
      : '';
    // D3 -- names wrap, quantities must NOT, or "50 each" breaks in half.
    const qty = escapeHtml(`${line?.qty ?? ''} ${line?.uom || ''}`.trim());
    return `
      <tr>
        <td style="padding:9px 0;border-bottom:1px solid #F0F0F0;vertical-align:top;">
          <span style="font-size:14px;color:#0F0F0F;">${name}</span>${tag}
        </td>
        <td align="right" style="padding:9px 0 9px 10px;border-bottom:1px solid #F0F0F0;vertical-align:top;white-space:nowrap;">
          <span style="font-size:14px;font-weight:700;color:#0F0F0F;">${qty}</span>
        </td>
      </tr>
    `;
  }).join('');

  // D4 -- straight out of the competitive research. Oracle E-Business Suite
  // carries a "PO: Notification Lines Display Limit" profile option AND has a
  // documented note about the requisition notification failing to honour it, so
  // an uncapped list is a known way to get this wrong rather than a hypothetical.
  // The heading states the TRUE total even when the list is cut, because a cap
  // that hid how much was being approved would be worse than no cap at all.
  const moreRow = hidden > 0 ? `
      <tr>
        <td colspan="2" style="padding:9px 0 0;font-size:13px;color:#7A7A7A;">
          + ${hidden} more item${hidden === 1 ? '' : 's'} — open the request to see them all
        </td>
      </tr>
  ` : '';

  return `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      <tr>
        <td style="padding:0 0 7px;border-bottom:2px solid #E8E8E8;">
          <span style="font-size:11px;font-weight:700;color:#9A9A9A;text-transform:uppercase;letter-spacing:0.8px;">${escapeHtml(heading)} (${total})</span>
        </td>
        <td align="right" style="padding:0 0 7px 10px;border-bottom:2px solid #E8E8E8;">
          <span style="font-size:11px;font-weight:700;color:#9A9A9A;text-transform:uppercase;letter-spacing:0.8px;">Qty</span>
        </td>
      </tr>
      ${rows}
      ${moreRow}
    </table>
  `;
}

// ── EMAIL TEMPLATES ──────────────────────────────────────────────────────────

export function emailToolTaggedOut(toolName, branch, reason, taggedBy, station, serialNumber, toolId) {
  const rows = detailTable(
    detailRow('Tool', toolName) +
    detailRow('Branch', branch) +
    detailRow('Station / truck', station) +
    detailRow('Serial number', serialNumber) +
    detailRow('Reason', reason) +
    detailRow('Tagged out by', taggedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A tool has been tagged out of service and requires attention. Review the tag-out log and assign it to repair or schedule an assessment.
    </p>
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#9A5F00;">
      Open the tag-out to update its status or start a repair.
    </div>
  `;

  return emailWrapper({
    module: 'tools',
    icon: '⚠️',
    eventLabel: 'Tool Tagged Out',
    // #126 + gate decision M3, Session 78v. This email had NO destination:
    // its button read 'Open Stede' and landed on the module home, so the
    // reader had to find by hand the record the email had just told them
    // about. The page key is the one the bell notification already passes
    // at this call site, so the two cannot disagree about where to go.
    ctaLabel: 'Open this tag-out',
    ctaUrl: deepLinkUrl('tagout', toolId),
    bodyContent: body,
  });
}

export function emailSpareRequestSubmitted(toolName, branchName, managerName, requestId) {
  const rows = detailTable(
    detailRow('Tool requested', toolName) +
    detailRow('Requesting branch', branchName) +
    detailRow('Requested by', managerName)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A spare tool request has been submitted and is waiting for your review. Open it to approve or deny.
    </p>
    ${rows}
    <div style="background:#E3F2FD;border-left:4px solid #1976D2;border-radius:4px;padding:12px 16px;font-size:13px;color:#1565C0;">
      Once approved, the tool will be prepared for shipment from the Dallas spare pool.
    </div>
  `;

  return emailWrapper({
    module: 'tools',
    icon: '📋',
    eventLabel: 'New Spare Request',
    // #126 + gate decision M3, Session 78v. This email had NO destination:
    // its button read 'Open Stede' and landed on the module home, so the
    // reader had to find by hand the record the email had just told them
    // about. The page key is the one the bell notification already passes
    // at this call site, so the two cannot disagree about where to go.
    ctaLabel: 'Open this request',
    ctaUrl: deepLinkUrl('requests', requestId),
    bodyContent: body,
  });
}

export function emailToolShipped(toolName, branchName, shippedBy, requestId) {
  const rows = detailTable(
    detailRow('Tool', toolName) +
    detailRow('Shipped to', branchName) +
    detailRow('Shipped by', shippedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A tool has been shipped to your branch from the Dallas spare pool. Please confirm receipt in the system when it arrives.
    </p>
    ${rows}
    <div style="background:#E8F5E9;border-left:4px solid #39D353;border-radius:4px;padding:12px 16px;font-size:13px;color:#2E7D32;">
      Open the request and press <strong>Confirm receipt</strong> when the tool arrives.
    </div>
  `;

  return emailWrapper({
    module: 'tools',
    icon: '📦',
    eventLabel: 'Tool Shipped',
    // #126 + gate decision M3, Session 78v. This email had NO destination:
    // its button read 'Open Stede' and landed on the module home, so the
    // reader had to find by hand the record the email had just told them
    // about. The page key is the one the bell notification already passes
    // at this call site, so the two cannot disagree about where to go.
    ctaLabel: 'Open this request',
    ctaUrl: deepLinkUrl('requests', requestId),
    bodyContent: body,
  });
}

export function emailInspectionDiscrepancy(stationTruck, branch, discrepancies, notes, conductedBy, inspectionId) {
  const rows = detailTable(
    detailRow('Station / truck', stationTruck) +
    detailRow('Branch', branch) +
    detailRow('Discrepancies found', String(discrepancies)) +
    detailRow('Conducted by', conductedBy) +
    detailRow('Notes', notes)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      An inspection has been logged with discrepancies that require your attention. Review the details below and follow up as needed.
    </p>
    ${rows}
    <div style="background:#FFEBEE;border-left:4px solid #C62828;border-radius:4px;padding:12px 16px;font-size:13px;color:#C62828;">
      Open the inspection to review the full record and act on anything flagged.
    </div>
  `;

  return emailWrapper({
    module: 'safety',
    icon: '🔍',
    eventLabel: 'Inspection Discrepancy',
    // #126 + gate decision M3, Session 78v. This email had NO destination:
    // its button read 'Open Stede' and landed on the module home, so the
    // reader had to find by hand the record the email had just told them
    // about. The page key is the one the bell notification already passes
    // at this call site, so the two cannot disagree about where to go.
    ctaLabel: 'Open this inspection',
    ctaUrl: deepLinkUrl('inspect', inspectionId),
    bodyContent: body,
  });
}

// Session 55 note: copy corrected. Previously told the manager to "place an order with
// purchasing" — wrong action at this stage, since the manager's real task on receiving this
// email is approve/deny (RequestsInboxPage's "needs approval" tab). Purchasing only gets
// involved after approval, via emailRequestApprovedForPurchasing() below. Signature also
// gained qtyRequested/uom — the original only carried qtyOnHand, which doesn't tell an
// approving manager how much is actually being asked for. No live caller existed before this
// session, so the signature change is safe.
// `lines` (Session 79.4, D1/D5/D9) is appended LAST so every existing positional
// argument keeps its place. With fewer than two lines it is inert and this
// template renders byte-identically to before.
export function emailRestockRequest(itemName, requestNumber, branch, requestedBy, qtyRequested, uom, qtyOnHand, requestId, lines) {
  const linesTable = lineItemsTable(lines, 'Items requested');
  const isMulti = !!linesTable;

  const rows = detailTable(
    // D5 -- on a multi-item request the Item row is replaced by the table above,
    // and "Qty requested" is REMOVED rather than reworded. It read "3 items"
    // directly beneath a row containing the numbers 3, 7 and 2, so the count of
    // LINES read as a quantity. As the table's heading the same 3 is unambiguous,
    // because it sits above a list of three rows instead of beside their numbers.
    (isMulti ? '' : detailRow('Item', itemName)) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Requested by', requestedBy) +
    (isMulti ? '' : detailRow('Qty requested', `${qtyRequested} ${uom || ''}`.trim())) +
    // Single-item only, and that is unchanged: one on-hand figure cannot describe
    // several items. Per-line on-hand is the obvious next improvement and was
    // deliberately left OUT of the 79.4 gate (D6) -- it needs a stock lookup per
    // line, which getQtyOnHand() does not do, and adding a query inside the
    // notify path is its own change with its own failure mode.
    detailRow('Qty on hand', qtyOnHand != null ? String(qtyOnHand) : null)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      ${isMulti
        ? `A team member has submitted a restock request for ${lines.length} inventory items. Review the request and approve or deny it.`
        : 'A team member has submitted a restock request for a low or out-of-stock inventory item. Review the request and approve or deny it.'}
    </p>
    ${linesTable}
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#9A5F00;">
      Reference number <strong>${requestNumber}</strong> will carry through to purchasing and receiving once approved.
    </div>
  `;

  return emailWrapper({
    module: 'inventory',
    icon: '📦',
    eventLabel: 'Restock Requested — Needs Approval',
    ctaLabel: 'Open this request',
    ctaUrl: deepLinkUrl('requests_inbox', requestId),
    bodyContent: body,
  });
}

// New Session 55 — off-catalog sibling of emailRestockRequest(), trigger #1. A manager
// approving an off-catalog request has no catalog listing to check against, so this carries
// the free-text description (and photo, if one was attached) instead of an on-hand count.
export function emailRestockRequestOffCatalog(description, requestNumber, branch, requestedBy, qty, uom, vendorSuggestion, photoUrl, requestId) {
  const rows = detailTable(
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Requested by', requestedBy) +
    detailRow('Qty requested', `${qty} ${uom || ''}`.trim()) +
    detailRow('Suggested vendor', vendorSuggestion)
  );

  const photoBlock = photoUrl ? `
    <div style="margin-bottom:20px;">
      <img src="${photoUrl}" alt="Request photo" style="max-width:100%;border-radius:8px;display:block;" />
    </div>
  ` : '';

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 16px;">
      A team member has requested an item that isn't in the catalog. Review the description below and approve or deny.
    </p>
    <div style="background:#F7F7F7;border-radius:6px;padding:12px 16px;margin-bottom:20px;font-size:14px;color:#0F0F0F;line-height:1.5;">
      ${description}
    </div>
    ${photoBlock}
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#9A5F00;">
      Off-catalog items have no vendor pricing on file yet — purchasing will source and price this after approval.
    </div>
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '📝',
    eventLabel: 'Off-Catalog Request — Needs Approval',
    ctaLabel: 'Open this request',
    ctaUrl: deepLinkUrl('requests_inbox', requestId),
    bodyContent: body,
  });
}

// New Session 55 — trigger #2. Fires when a manager approves a request, to purchasing
// (getPurchasingEmails()), not to the branch. No prior template targeted this audience.
// `lines` appended last, exactly as on emailRestockRequest() -- D9, because
// changing one of these two and not the other leaves the same run-on defect on
// the very next screen along.
export function emailRequestApprovedForPurchasing(itemName, requestNumber, branch, approvedBy, qty, uom, isOffCatalog, description, requestId, lines) {
  const linesTable = lineItemsTable(lines, 'Items approved');
  const isMulti = !!linesTable;

  const rows = detailTable(
    (isMulti ? '' : detailRow('Item', isOffCatalog ? (description || itemName) : itemName)) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Approved by', approvedBy) +
    // Same reasoning as the restock template: on a multi-item request this row
    // said "3 items" while the lines held 3, 7 and 2. The count now lives in the
    // table heading where it cannot be read as a quantity.
    (isMulti ? '' : detailRow('Qty needed', `${qty} ${uom || ''}`.trim()))
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      ${isMulti
        ? `A material request for ${lines.length} items has been approved and is ready to order.${isOffCatalog ? ' At least one line is off-catalog — sourcing and pricing still need to be established.' : ''}`
        : `A material request has been approved and is ready to order.${isOffCatalog ? ' This is an off-catalog item — sourcing and pricing still need to be established.' : ''}`}
    </p>
    ${linesTable}
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#9A5F00;">
      Reference number <strong>${requestNumber}</strong> must be included on the purchase order so the inbound shipment can be matched on receipt.
    </div>
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '✅',
    eventLabel: 'Approved — Ready to Order',
    ctaLabel: 'Open this order',
    ctaUrl: deepLinkUrl('purchasing_queue', requestId),
    bodyContent: body,
  });
}

// New Session 55 — trigger #3. Fires from PurchasingQueuePage's submitMarkOrdered(), which
// previously called sendEmail(null, ...) — a guaranteed no-op given sendEmail()'s own guard
// clause. Goes to the requesting branch's managers, not the requester.
export function emailRequestOrdered(itemName, requestNumber, branch, vendorName, poRef, eta, orderedBy, requestId) {
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Vendor', vendorName) +
    detailRow('PO ref', poRef) +
    detailRow('Est. arrival', eta) +
    detailRow('Ordered by', orderedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      Purchasing has placed an order for this restock request. The purchase order sent to the vendor can be downloaded from the request itself — no action needed, and you'll be notified again when it arrives.
    </p>
    ${rows}
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '🛒',
    eventLabel: 'Order Placed',
    ctaLabel: 'Open this order',
    ctaUrl: deepLinkUrl('requests_inbox', requestId),
    bodyContent: body,
  });
}

// New Session 55 — trigger #4. Goes to branch managers only, by explicit decision — the
// requester already sees this in-app via the existing sendNotification() call at both
// call sites (RequestsInboxPage's ReceiveSheetPortal and InventoryActionCatalog's
// submitReceive()), and does not also get the email.
export function emailRequestReceived(itemName, requestNumber, branch, qtyReceived, uom, receivedBy, requestId) {
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Qty received', `${qtyReceived} ${uom || ''}`.trim()) +
    detailRow('Received by', receivedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      This restock request has arrived and stock has been updated at ${branch}.
    </p>
    ${rows}
  `;

  return emailWrapper({
    module: 'inventory',
    icon: '✓',
    eventLabel: 'Restock Received',
    ctaLabel: 'Open this delivery',
    ctaUrl: deepLinkUrl('requests_inbox', requestId),
    bodyContent: body,
  });
}

export function emailBranchSpareRequest(toolName, branch, department, station, requestedBy, requestId) {
  const rows = detailTable(
    detailRow('Tool needed', toolName) +
    detailRow('Branch', branch) +
    detailRow('Department', department) +
    detailRow('Station / truck', station) +
    detailRow('Requested by', requestedBy)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A team member has tagged out a tool and is requesting a replacement from the branch spare buffer. Review availability and assign a spare to their station.
    </p>
    ${rows}
    <div style="background:#FFF3E0;border-left:4px solid #F5A623;border-radius:4px;padding:12px 16px;font-size:13px;color:#9A5F00;">
      If no branch spare is available, submit a request to the Dallas spare pool to replenish your buffer.
    </div>
  `;

  return emailWrapper({
    module: 'tools',
    icon: '🔄',
    eventLabel: 'Branch Spare Requested',
    // #126 + gate decision M3, Session 78v. This email had NO destination:
    // its button read 'Open Stede' and landed on the module home, so the
    // reader had to find by hand the record the email had just told them
    // about. The page key is the one the bell notification already passes
    // at this call site, so the two cannot disagree about where to go.
    ctaLabel: 'Open this request',
    ctaUrl: deepLinkUrl('requests', requestId),
    bodyContent: body,
  });
}

// New Session 74b2 — Issue #191, trigger #5. Fires from PurchasingQueuePage's
// submitEnterInvoice() only when the discrepancy check trips — this is the email half
// of the same event whose in-app sendNotification() call already fires at that site.
export function emailInvoiceDiscrepancy(itemName, requestNumber, branch, orderedPrice, invoicePrice, invoiceRef, requestId) {
  const diff = invoicePrice - orderedPrice;
  const pct = orderedPrice > 0 ? ((diff / orderedPrice) * 100).toFixed(1) : '—';
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Ordered price', `$${orderedPrice.toFixed(2)}`) +
    detailRow('Invoice price', `$${invoicePrice.toFixed(2)}`) +
    detailRow('Difference', `${diff >= 0 ? '+' : ''}$${diff.toFixed(2)} (${pct}%)`) +
    detailRow('Invoice #', invoiceRef)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      An invoice was entered with a price that differs from what was agreed at order time by more than the configured threshold. Review the request and confirm this is correct.
    </p>
    ${rows}
    <div style="background:#FFEBEE;border-left:4px solid #A32D2D;border-radius:4px;padding:12px 16px;font-size:13px;color:#A32D2D;">
      Reference number <strong>${requestNumber}</strong> has been marked with a price discrepancy in the Purchasing Queue's history.
    </div>
  `;

  return emailWrapper({
    module: 'invoicing',
    icon: '⚠️',
    eventLabel: 'Invoice Price Discrepancy',
    // #384, Session 79.12 -- WAS deepLinkUrl('requests_inbox', ...), changed in
    // the same commit as the bell it mirrors. Gate decision G binds the two
    // together: an email and the bell for one event must land in the same
    // place or they drift into telling one reader two stories.
    //
    // THE LABEL FOLLOWS THE DESTINATION. "Open this request" was true of the
    // Requests inbox and is not true of Invoicing, and a CTA naming the wrong
    // thing is the accessibility anti-pattern gate decision E6 exists to stop.
    ctaLabel: 'Open Invoicing',
    ctaUrl: deepLinkUrl('purchasing_invoicing', requestId),
    bodyContent: body,
  });
}

// New Session — Issue #210, trigger #6. Fires when a Mark Ordered attempt exceeds
// the configured dollar threshold — goes to admin+executive, the only roles who
// can approve or deny it.
export function emailPurchaseApprovalNeeded(itemName, requestNumber, branch, orderTotal, thresholdAmount, requestId) {
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Order total', `$${orderTotal.toFixed(2)}`) +
    detailRow('Threshold', `$${parseFloat(thresholdAmount).toFixed(2)}`)
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A purchase order exceeds the configured approval threshold and is on hold until an admin or executive reviews it.
    </p>
    ${rows}
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '⏸',
    eventLabel: 'Purchase Approval Needed',
    ctaLabel: 'Open this order',
    ctaUrl: deepLinkUrl('purchasing_queue', requestId),
    bodyContent: body,
  });
}

// New Session — Issue #210, trigger #7. Fires when an admin/executive denies a
// threshold-flagged order — goes to whoever can act on the Purchasing Queue so
// the order can be revised and resubmitted.
export function emailPurchaseApprovalDenied(itemName, requestNumber, branch, reason, requestId) {
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Reason', reason || '—')
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      This purchase order was denied and needs revision before it can be resubmitted through Mark Ordered.
    </p>
    ${rows}
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '✕',
    eventLabel: 'Purchase Order Denied',
    ctaLabel: 'Open this order',
    ctaUrl: deepLinkUrl('purchasing_queue', requestId),
    bodyContent: body,
  });
}

// New Session 74p - Issue #256, trigger #8. Fires when an admin/executive approves a
// threshold-flagged order. The order has NOT been placed at this point - Stede
// transmits nothing to a vendor - so the copy asks for the call to be made, and the
// "Order placed" email still comes later from submitMarkOrdered().
export function emailPurchaseApprovalGranted(itemName, requestNumber, branch, approvedAmount, approverName, requestId) {
  const rows = detailTable(
    detailRow('Item', itemName) +
    detailRow('Request number', requestNumber) +
    detailRow('Branch', branch) +
    detailRow('Approved amount', approvedAmount != null ? `$${parseFloat(approvedAmount).toFixed(2)}` : '—') +
    detailRow('Approved by', approverName || '—')
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      This purchase order is approved and waiting in Needs Action. Place the order with the vendor, then mark it ordered. Going above the approved amount sends it back for approval.
    </p>
    ${rows}
  `;

  return emailWrapper({
    module: 'purchasing',
    icon: '✓',
    eventLabel: 'Purchase Approved',
    ctaLabel: 'Open this order',
    ctaUrl: deepLinkUrl('purchasing_queue', requestId),
    bodyContent: body,
  });
}
// ── INTER-BRANCH TRANSFER EMAIL ──────────────────────────────────────────────
// Issue #514, Session 79.7. Gate decision D29, approved by Colby at 79.6's close.
//
// WHY ONLY TWO TEMPLATES WHEN A TRANSFER HAS SIX NOTIFICATION POINTS.
//
// A transfer was entirely in-app until now: ask a branch to send material and
// the only thing that happened was a bell. Ask purchasing for the same material
// and you got an email. The two paths held people to different standards for
// the same act, and the transfer path is the one where somebody at ANOTHER
// branch -- who has no reason to open Stede that morning -- is the person who
// has to act.
//
// D29 splits the AUDIENCE rather than the event, and it departs from the
// instruction that produced it in one place. Colby asked for email to
// "approvers and fufillers at the manager level". The approver half is built
// here as asked. The fulfiller half is deliberately NOT, and the reason is not
// a design preference: 83% of non-desk workers have no regular email access.
// The person cutting steel is on a floor moving between jobs, so a queue they
// can see beats an inbox they will not open -- which is what the production
// queue already is. He took the correction and approved it the same evening.
//
// So: EMAIL the approver, and EMAIL the short close. Everything else stays a
// bell. NetSuite's own guidance is the same shape -- mail the person whose turn
// it is to act, and warn against notifying everybody at every stage.

// To the people who must agree to send the material. Deep-linked per #126.
//
// `lines` is [{ name, qty, uom }] and goes through the same lineItemsTable() a
// multi-item material request uses, so a five-item transfer reads as five rows
// rather than a comma-joined string in a 140px column -- 79.4's finding,
// inherited rather than re-derived.
//
// `stage`, #583 at Session 79.21b: 'supply' (the default, and everything this
// sent before) is the supplying branch being asked. 'ask' is the ASKING
// branch's own approvers, who since #528 must agree before anyone else is
// asked - a different reader, so a different sentence and a different button.
// A default rather than a required argument, so the arity guard in
// emailTemplates.test.js still reads eight and every existing caller is the
// supply email it always was.
export function emailTransferRequested(label, fromBranch, toBranch, requestedBy, mode, neededBy, lines, transferId, stage = 'supply') {
  const linesTable = lineItemsTable(lines, 'Items requested');
  const isMulti = !!linesTable;
  const isMake = mode === 'make';
  const isAsk = stage === 'ask';
  // #580 part a -- a branch asking its own shop to make something for its own
  // shelf. "Houston has asked Houston" reads as a mistake.
  const ownShelf = !!fromBranch && fromBranch === toBranch;

  const rows = detailTable(
    detailRow('Transfer', label) +
    detailRow('Sending branch', fromBranch) +
    detailRow('Receiving branch', toBranch) +
    detailRow('Requested by', requestedBy) +
    // BUILT FROM THE PARTS THAT EXIST, not by interpolating three values and
    // hoping. A template literal turns a missing value into the literal word
    // "undefined" and renders it in a real person's inbox looking deliberate --
    // which is exactly what shipped, and what Colby read back within minutes.
    // Filtering means a missing quantity produces a shorter line rather than a
    // wrong one, and an entirely empty line suppresses the row via detailRow().
    (isMulti ? '' : detailRow('Items', [lines?.[0]?.qty, lines?.[0]?.uom, lines?.[0]?.name]
      .filter(v => v != null && v !== '')
      .join(' ')
      .trim() || null)) +
    detailRow('Needed by', neededBy)
  );

  // THE TWO MODES ARE DIFFERENT REQUESTS AND THE MAIL SAYS SO. Taking something
  // off a shelf and manufacturing it are not the same commitment, and the
  // approver is agreeing to whichever one this is. Getting this wrong is the
  // 79.6 defect from the other side: the build offered "Today / Tomorrow / This
  // week" on a make request, and Colby's answer was that making 100 frames
  // "cant happen today, cant happen tomorrow and could maybe happen this week".
  const intro = isAsk
    ? `${escapeHtml(requestedBy || 'Someone on your team')} is asking ${escapeHtml(fromBranch)} to ${isMake ? '<strong>make</strong> material' : 'send material from stock'}. It waits on your approval before ${escapeHtml(fromBranch)} is asked. Approve or deny it in the Requests inbox.`
    : ownShelf
      ? `${escapeHtml(requestedBy || 'Someone')} has asked ${escapeHtml(toBranch)}'s own shop to <strong>make</strong> material for ${escapeHtml(toBranch)}'s shelf. Confirming commits the shop to making it, and you will be asked for a date you can promise.`
      : isMake
        ? `${escapeHtml(toBranch)} has asked ${escapeHtml(fromBranch)} to <strong>make</strong> material for them. Approving commits your shop to supplying it — making it, or sending it from stock — and you will be asked for a date you can promise.`
        : `${escapeHtml(toBranch)} has asked ${escapeHtml(fromBranch)} to send material from stock. Approving commits your branch's material, and you will be asked for a date you can promise.`;

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      ${intro}
    </p>
    ${linesTable}
    ${rows}
    <div style="background:#E8F5EE;border-left:4px solid #1D9E75;border-radius:4px;padding:12px 16px;font-size:13px;color:#0D6B4C;">
      No money moves between branches. This is D&amp;R's own material changing shelves, so there is no purchase order and no invoice.
    </div>
  `;

  return emailWrapper({
    module: 'inventory',
    icon: isMake ? '🔧' : '📦',
    eventLabel: isAsk
      ? 'Transfer Request — Needs Your Approval'
      : (isMake ? 'Material to Make — Needs Approval' : 'Transfer Requested — Needs Approval'),
    ctaLabel: isAsk ? 'Open the Requests inbox' : 'Review this transfer',
    // The first stage is approved in the Requests inbox; the transfer's own
    // page has no Approve at that stage (#583).
    // #605, Session 79.21c -- THE ASK-STAGE MAIL HAD THE BELL'S DEFECT TOO,
    // and it was nearly missed because the stage split here looks like it is
    // already doing the work. It sent the approver to the inbox with NO id, so
    // the mail named one transfer and opened a list of everything waiting on
    // them. The namespaced id is what lets the inbox address a transfer at all:
    // its link id has always been the material-request namespace.
    ctaUrl: isAsk
      ? deepLinkUrl('requests_inbox', transferLinkId(transferId))
      : deepLinkUrl('transfers', transferId),
    bodyContent: body,
  });
}

// The short close. EMAILED rather than belled because it carries a money figure
// and reaches people who are not in the app that day -- gate D29, and the same
// reasoning D11/D18 use for why this notification has no preference toggle:
// stock was lost and the message carries its value.
//
// NO STOCK MOVES on a short close. The sender is already down the full shipped
// quantity, so a write-off here would take the goods off them twice. What this
// records is that the balance is never arriving.
export function emailTransferClosedShort(label, fromBranch, toBranch, shortValue, reason, transferId) {
  const money = Number(shortValue) > 0
    ? Number(shortValue).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : null;

  const rows = detailTable(
    detailRow('Transfer', label) +
    detailRow('Sent from', fromBranch) +
    detailRow('Going to', toBranch) +
    detailRow('Value never arrived', money) +
    detailRow('Reason given', escapeHtml(reason))
  );

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      A transfer has been closed with material still outstanding. The balance is
      not coming, and the value below has left the company's stock without
      arriving anywhere.
    </p>
    ${rows}
    <div style="background:#FDECEC;border-left:4px solid #A32D2D;border-radius:4px;padding:12px 16px;font-size:13px;color:#7C2020;">
      Nothing further will move on this transfer. If the material does turn up later it needs a new receipt, not a reopening of this one.
    </div>
  `;

  return emailWrapper({
    module: 'inventory',
    icon: '⚠️',
    eventLabel: 'Transfer Closed Short',
    ctaLabel: 'Open this transfer',
    ctaUrl: deepLinkUrl('transfers', transferId),
    bodyContent: body,
  });
}

// ── A LOAD ARRIVING — #517, Session 79.10, gate D68 ──────────────────────────
//
// COLBY'S RULING, overturning the gate's own D60, and his case is the argument.
// Four hundred frames cover three orders at 200, 150 and 50. Seventy-five
// arrive. Nobody is told. The fifty-frame order misses its date for no reason
// and a customer is upset.
//
// AN ARRIVAL IS NOT AN INTERRUPTION, IT RELEASES WORK. The recommendation
// treated it as a volume problem — how many times do we interrupt somebody —
// and that was the wrong frame. A page somebody has to remember to open cannot
// do this job, so the arrival itself is the notification.
//
// IT GOES TO THE APPROVER RATHER THAN THE DRIVER, which is #514's D29 holding:
// 83% of non-desk workers have no reliable email, so mailing the person
// standing at the rack is unreliable by construction rather than by preference.
//
// `outstanding` is what is STILL OWED on the whole request after this load —
// zero means the request is finished. It is the number that decides whether
// somebody can release the last job, so it is stated rather than implied.
//
// `ended` (#599, Session 79.21b) is the third state: the transfer had already
// ended as "nothing more coming" when this load was counted -- the asking
// branch stopped the rest, or the sender closed it. Nothing is owed AND not
// everything asked for arrived, so neither of the other two sentences is true.
// It has a default, so the arity guard in emailTemplates.test.js still counts 8.
export function emailTransferLoadArrived(
  label, loadRef, fromBranch, toBranch, lines, outstanding, note, transferId, ended = false
) {
  const stillOwed = Number(outstanding) || 0;
  const complete = !ended && stillOwed <= 0;
  // #685, Session 79.22b.4.3 -- A ONE-ITEM LOAD NAMES WHAT ARRIVED. The line
  // table below draws nothing for fewer than two lines, which is right for the
  // three other emails that use it (they name a single item in their own row)
  // and left this one saying neither what landed nor how many. The count is
  // what was COUNTED IN (countInLoad() passes it), and the unit is left off, as
  // the bell and the load sheet leave it (#701): "12 Dibond White 5x10".
  const only = Array.isArray(lines) && lines.length === 1 ? lines[0] : null;
  const whatArrived = only && Number(only.qty) > 0
    ? escapeHtml(`${Number(only.qty)} ${only.name || (Number(only.qty) === 1 ? 'item' : 'items')}`)
    : null;

  const rows = detailTable(
    detailRow('Transfer', escapeHtml(label)) +
    detailRow('Load', escapeHtml(loadRef)) +
    detailRow('Came from', escapeHtml(fromBranch)) +
    detailRow('Arrived at', escapeHtml(toBranch)) +
    detailRow('What arrived', whatArrived) +
    detailRow('Still owed after this', complete || ended ? null : String(stillOwed)) +
    // #508 — user-typed text never reaches the template unescaped. The driver
    // types this at the rack and it is the one field on this email a person
    // wrote by hand.
    detailRow('Noted on arrival', note ? escapeHtml(note) : null)
  );

  const headline = ended
    ? 'Material has arrived and been booked in. Nothing more is coming on this transfer.'
    : complete
      ? 'The last of this transfer has arrived and been booked in. Nothing further is outstanding.'
      : 'Material has arrived and been booked in. The rest of this transfer is still on its way.';

  // Green when the request is finished, amber while a balance is still owed.
  // Both are the app's own module colours rather than invented hex — #414, and
  // the strip takes a module for the same reason.
  // Ended: neutral grey, because nothing is wrong and nothing is pending --
  // somebody decided the rest was not needed. #3A4550 on #F4F6F8 clears 9.
  const banner = ended
    ? `<div style="background:#F4F6F8;border-left:4px solid #8A97A3;border-radius:4px;padding:12px 16px;font-size:13px;color:#3A4550;">
         This was the last of it. The rest of this transfer will not be sent.
       </div>`
    : complete
    ? `<div style="background:#E7F6F0;border-left:4px solid #1D9E75;border-radius:4px;padding:12px 16px;font-size:13px;color:#146B50;">
         Everything asked for has now arrived. Work waiting on this material can be released.
       </div>`
    : `<div style="background:#FDF3E3;border-left:4px solid #EF9F27;border-radius:4px;padding:12px 16px;font-size:13px;color:#8A5A10;">
         ${stillOwed} still to come on this transfer. What has landed can be used now.
       </div>`;

  const body = `
    <p style="font-size:15px;color:#0F0F0F;margin:0 0 20px;">
      ${headline}
    </p>
    ${rows}
    ${lineItemsTable(lines, 'What arrived')}
    ${banner}
  `;

  return emailWrapper({
    module: 'inventory',
    icon: '📦',
    eventLabel: complete ? 'Transfer Complete' : 'Material Arrived',
    ctaLabel: 'Open this transfer',
    ctaUrl: deepLinkUrl('transfers', transferId),
    bodyContent: body,
  });
}
